import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { colors } from '@packages/core/styles';
import { Icon, TextBadge, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

/** The label shown on the primary DTC code badge. */
const PRIMARY = 'PRIMARY';

/** The label shown when freeze frame data has been saved to SSM. */
const FRAME_FREEZED = 'FFD Saved to SSM';

const textBadgeStyles = { paddingVertical: 2, paddingHorizontal: 8 };
const textColorStyles = { color: colors.blueOne };
const iconStyles = { paddingBottom: 1 };

/**
 * Props for the {@link DtcCodes} component.
 */
type DtcCodesProps = {
    /** The list of DTC codes to display. */
    dtcCodeData: DtcCodeEntity[];
};

/**
 * Renders a read-only list of DTC codes for a submitted Techline case. Each code displays its value
 * along with badges indicating primary status and freeze frame data presence.
 *
 * @param props - the component props
 * @returns the DTC code list, or null if no codes are present
 */
export function DtcCodes({ dtcCodeData }: DtcCodesProps) {
    const classes = useStyles();
    if (!dtcCodeData || dtcCodeData.length === 0) {
        return null;
    }

    return (
        <div className={classes.container}>
            {dtcCodeData.map((dtc, index) => (
                <div className={classes.item} key={index}>
                    <div className={classes.itemInnerLeft}>
                        <Typography color="textDarkPrimary" variant="h5">
                            {dtc.code}
                        </Typography>
                    </div>
                    <div className={classes.itemInnerRight}>
                        {dtc?.hasFreezeFrameDataInSSM && <TextBadge style={textBadgeStyles}>{FRAME_FREEZED}</TextBadge>}
                        {dtc?.isPrimary && (
                            <TextBadge
                                icon={<Icon color="blueOne" name="star-filled" size={12} style={iconStyles} />}
                                style={textBadgeStyles}
                                textStyle={textColorStyles}
                                variant="blue"
                            >
                                {PRIMARY}
                            </TextBadge>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
    },
    item: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayFour}`,
        borderRadius: 4,
        gap: 8,
        padding: 24,
    },
    itemInnerLeft: {
        flex: 1,
        minWidth: 0,
    },
    itemInnerRight: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
});
